import { CountryApprovalRequirement } from 'api/swagger/ApiClient';
import { VariousReportsApi } from 'api/various-reports';
import { autoinject } from 'aurelia-framework';
import { Router } from 'aurelia-router';
import { watch } from 'aurelia-watch-decorator';
import { tryExecute } from 'framework/error_handling/error-utils';
import { downloadFile } from 'framework/script_utilities/file-utils';
import { TextHighlightValueConverter } from 'framework/value_converters/text-highlight';
import { Country } from 'model/business-keywords';
import { CountryVisit } from 'model/forms/travel-request';
import { CountryPair } from 'model/support-types';
import { KeywordsService } from 'services/keywords-service';
import { SafetyService } from 'services/safety-service';
import { requireServices } from 'support/activation';
import * as E from 'support/errors';
import { HOME_ROUTE } from 'support/settings';
import { DataTransferer } from 'support/shared_models/data-transferer';

@autoinject
export class CountriesListScreen {
  filterText: string = null;
  aprovalRequired = false;
  countries: CountryPair[] = null;
  createRequestRoute = `#/${HOME_ROUTE}/requests/new`;

  constructor(private keywords: KeywordsService, private safety: SafetyService, private api: VariousReportsApi, private router: Router, private transferer: DataTransferer) { }

  async canActivate() {
    await requireServices(this.keywords, this.safety);
  }

  bind() {
    this.queryCountries();
  }

  @watch((x: CountriesListScreen) => x.filterText)
  @watch((x: CountriesListScreen) => x.aprovalRequired)
  filterMutated() {
    tryExecute(() => this.queryCountries(), E.FILTER_ERROR_MESSAGE, E.FILTER_ERROR_TITLE, true);
  }

  queryCountries() {
    this.countries = this.filterCountries();
  }

  filterCountries() {
    const filterText = this.filterText?.trim().toLowerCase();
    return this.keywords.countries
      .map(c => ({ country: c, settings: this.safety.countrySettings.get(c.id) }))
      .filter(x => x.settings != null)
      .filter(x => filterText == null || x.country.name.toLowerCase().includes(filterText) || x.settings.conditionMessage?.toLowerCase().includes(filterText))
      .filter(x => !this.aprovalRequired || x.settings.approvalRequirementValue !== CountryApprovalRequirement.NotRequired)
      .map(x => this.extendCountry(x, filterText));
  }

  private extendCountry(pair: CountryPair, expression: string) {
    const converter = new TextHighlightValueConverter();
    const highlight = (line: string) => converter.toView(line, expression)?.map(s => s.highlight ? `<span class="--highlight">${s.substring}</span>` : s.substring).join('');
    pair['highlightedName'] = highlight(pair.country.name);
    pair['approvalDescription'] = this.keywords.approvalLevels.find(l => l.id === pair.settings.approvalLevel.id)?.description;
    pair['approvalRequired'] = pair.settings.approvalRequirementValue !== CountryApprovalRequirement.NotRequired;
    return pair;
  }

  async downloadSpreadsheet() {
    const report = await this.api.getCountriesReport();
    const url = URL.createObjectURL(report.data);
    try { downloadFile(url, report.fileName); }
    finally { URL.revokeObjectURL(url); }
  }

  createRequest(country: Country) {
    const initialCountries: CountryVisit[] = [{ country: country, transit: false }];
    this.transferer.set('INITIAL-COUNTRIES', initialCountries);
    this.router.navigate(this.createRequestRoute);
  }
}
